import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { PatternProduct } from '@/shared/composables/patternProducts/patternProducts.types';

export const usePatternProductsStore = defineStore('pattern-products', () => {
  /**
   * Core state - the source of truth for pattern products
   * Pattern products for the currently selected size.
   * Contains ALL products (including hidden/deleted) with user modifications.
   * Gets completely replaced on size changes but preserves user changes where possible.
   */
  const allPatternProductsForSize = ref<PatternProduct[]>([]);
  const currentSize = ref<string>('');

  const isValidPatternProduct = (pp: Partial<PatternProduct>) =>
    !!(pp.product_id && pp.variant_id);

  const hasValidIdentity = (pp: PatternProduct) => isValidPatternProduct(pp);

  const hasRequiredData = (pp: PatternProduct) =>
    pp.quantity > 0 && isValidPatternProduct(pp);

  // Core computed properties
  const activePatternProducts = computed<PatternProduct[]>(() =>
    allPatternProductsForSize.value.filter(hasRequiredData)
  );

  // Initialize store with data
  const initialize = (
    initialPatternProducts: PatternProduct[],
    size?: string
  ) => {
    allPatternProductsForSize.value = [...initialPatternProducts];
    if (size) currentSize.value = size;
  };

  const handleSizeChange = (size: string, newSizeData: PatternProduct[]) => {
    if (!newSizeData) return;

    const newPatternProducts: PatternProduct[] = [];
    const matchedProducts = new Set<PatternProduct>();

    // Match by product_id only (not variant_id) so a user-selected yarn color
    // is preserved across a size change instead of reverting to the new
    // size's default variant.
    newSizeData.forEach((newPP) => {
      if (!isValidPatternProduct(newPP)) return;

      const existingProduct = allPatternProductsForSize.value.find(
        (currentPP) =>
          currentPP.product_id === newPP.product_id &&
          !matchedProducts.has(currentPP)
      );

      if (existingProduct) {
        newPatternProducts.push({
          ...existingProduct,
          // Only a variant the shopper explicitly picked survives the size
          // change - otherwise adopt the new size's own default variant
          // (sizes can legitimately default to different variants).
          variant_id: existingProduct.isUserSelectedVariant
            ? existingProduct.variant_id
            : newPP.variant_id,
          quantity: newPP.quantity ?? 1
        });
        matchedProducts.add(existingProduct);
      } else {
        newPatternProducts.push(newPP);
      }
    });

    // Products that are no longer in the new size are kept for reference but
    // marked as deleted with quantity 0.
    const deletedPatternProducts: PatternProduct[] =
      allPatternProductsForSize.value
        .filter((currentPP) => !matchedProducts.has(currentPP))
        .map((currentPP) => ({ ...currentPP, quantity: 0 }));

    // Update state
    allPatternProductsForSize.value = [
      ...newPatternProducts,
      ...deletedPatternProducts
    ];
    currentSize.value = size;
  };

  // Update quantity by index in the source array
  const updateQuantityBySourceIndex = (
    sourceIndex: number,
    quantity: number
  ) => {
    if (allPatternProductsForSize.value[sourceIndex]) {
      allPatternProductsForSize.value[sourceIndex] = {
        ...allPatternProductsForSize.value[sourceIndex],
        quantity
      };
    }
  };

  // Update variant by index in the source array
  const updateVariantBySourceIndex = (
    sourceIndex: number,
    productId: string,
    variantId: string
  ) => {
    const patternProduct = allPatternProductsForSize.value[sourceIndex];
    if (patternProduct && patternProduct.product_id === productId) {
      allPatternProductsForSize.value[sourceIndex] = {
        ...allPatternProductsForSize.value[sourceIndex],
        variant_id: variantId,
        isUserSelectedVariant: true
      };
    }
  };

  return {
    // State
    allPatternProductsForSize,
    currentSize,

    // Computed
    activePatternProducts,

    // Actions
    initialize,
    handleSizeChange,
    updateQuantityBySourceIndex,
    updateVariantBySourceIndex,

    // Utilities (for composable use)
    hasRequiredData,
    hasValidIdentity
  };
});
